import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ServerCodes,
  ToastTypes,
  ErrorsType,
  GlobalToastTypeNames,
} from 'types';

import {
  useChangeGroupMutation,
  CustomErrorType,
  ChangeGroupMutationVariables,
} from 'core/graphql';
import { useToasts } from 'core/hooks';
import { IEditGroupPopupValues } from 'molecules';
import { GroupsQuery, EditingGroupQuery } from 'core/operations/queries';

import { groupErrorReducer, getGroupErrors } from './reducer';
import { ILessonTimeDay, lessonTimeFormErrors } from './lessonTimeErrors';
import { useGroupPermissions } from './useGroupPermissions';

type UpdateGroupType = [
  (variables: ChangeGroupMutationVariables) => void,
  {
    success: boolean;
    loading: boolean;
    errors: ErrorsType<IEditGroupPopupValues> | null;
  },
];

export const useUpdateGroupMutation = (): UpdateGroupType => {
  const { t } = useTranslation();
  // Те же переменные, что у списка групп (поля по правам роли).
  const { canReadGroupStaffFields } = useGroupPermissions();
  const [changeGroupMutation, { data, loading }] = useChangeGroupMutation({
    onError: (err) => err,
  });

  const [errors, dispatch] = useReducer(groupErrorReducer, null);
  const { addToast } = useToasts();
  /** Дни последней отправки — чтобы подсветить «конец» у нужного (F8). */
  const sentDays = useRef<ILessonTimeDay[]>([]);

  useEffect(() => {
    const serverErrors = data?.updateGroup?.customErrors;
    if (serverErrors && serverErrors.length) {
      // Перепроверка F8: длительность занятия — под «концом».
      const lessonTime = lessonTimeFormErrors(serverErrors, sentDays.current);
      if (lessonTime) {
        dispatch({
          type: ServerCodes.lessonTime,
          payload: (lessonTime as unknown) as ErrorsType<IEditGroupPopupValues>,
        });
        return;
      }
      const errorsFound = getGroupErrors(serverErrors as CustomErrorType[]);

      switch (true) {
        case errorsFound.groupExist:
          dispatch({ type: ServerCodes.groupExist });
          break;
        case errorsFound.lessonExist:
          dispatch({ type: ServerCodes.lessonExist });
          break;

        default:
          addToast({
            data: {
              title: `${t('common.somethingWentWrong')}`,
              type: ToastTypes.error,
            },
            type: GlobalToastTypeNames.GroupEdit,
          });
      }
    }
  }, [data?.updateGroup?.customErrors, addToast, t]);

  const updateGroup = useCallback(
    (variables: ChangeGroupMutationVariables) => {
      sentDays.current = variables.groupInput.daysOfClass || [];
      return changeGroupMutation({
        variables,
        refetchQueries: [
          {
            query: GroupsQuery,
            variables: {
              first: 10,
              withStaffFields: canReadGroupStaffFields,
            },
          },
          {
            query: EditingGroupQuery,
            variables: {
              groupId: variables.groupId.toString(),
            },
          },
        ],
      });
    },
    [changeGroupMutation, canReadGroupStaffFields],
  );

  return [
    updateGroup,
    {
      success: Boolean(data?.updateGroup?.success),
      loading,
      errors,
    },
  ];
};
